import { useState, useCallback, useEffect, useContext } from "react";

import { useTranslation } from "react-i18next";
import useAuthorization from "src/custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import AddNewEvent from "./AddNewEvent/AddNewEvent";
import { State } from "./interfaces";
import LegalEntityEvent from "./LegalEntityEvent/LegalEntityEvent";
import { Subheader } from "./styles";
import ReportTableHeader from "../../../../containers/DocumentsReport/ReportTableHeader/ReportTableHeader";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import {
  EventTypeQuickfilter,
  useEventTypeQuickFilter,
} from "../../../../containers/Quickfilters/EventTypeQuickfilter";
import QuickfiltersContainer from "../../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";

import useSearchOptions from "../../../../custom-hooks/useSearchOptions/useSearchOptions";

import { DocumentCategoryDTO } from "../../../../models/document-categories/document-category-dto";
import { SearchOptions } from "../../../../models/search-options/search-options";
import CommonPagination from "../../../../reusable-components/CommonPagination/CommonPagination";
import { loadDocumentCategory } from "../../../../services/document-categories/document-categories-api.service";
import { loadLegalEntityEvents } from "../../../../services/legal-entity-events/legal-entity-events-api.service";

import { DOCUMENT_CATEGORIES } from "../../../../utils/constants/constants";
import { FT_AACC } from "../../../../utils/constants/filter-tables-constants";

import { ComplianceDocumentsContext } from "../ComplianceDocuments";

const DEFAULT_PAGE_SIZE = 10;

const AssembliesAuditsCorporateChanges = () => {
  const tRP = "pages.document_reports.compliance_documents.assemblies_audits_corporate_changes";
  const { t } = useTranslation();
  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter, legalEntities },
  } = useContext(ComplianceDocumentsContext);
  const { eventTypeQuickFilter, setEventTypeQuickFilter } = useEventTypeQuickFilter();

  const [corporateChanges, setCorporateChanges] = useState<DocumentCategoryDTO | null>(null);

  const hasAccessToPage = useAuthorization([
    AUTHORIZATIONS.LegalEntityAccess.RunAccountingReports,
    AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting,
  ]);

  const loadCorporateChangesCategory = useCallback(async () => {
    const { items } = await loadDocumentCategory(DOCUMENT_CATEGORIES.CORPORATE_CHANGES.id);

    setCorporateChanges(items[0]);
  }, []);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    totalItems: 0,
    page: {
      pageNumber: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
  });

  const handleChangePageNumber = (pageNumber: number) =>
    setState((state) => ({
      ...state,
      page: { ...state.page, pageNumber: pageNumber - 1 },
    }));

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(FT_AACC);

  const loadLegalEntityEventsCallback = useCallback(
    async (searchOptions?: SearchOptions, signal?: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadLegalEntityEvents(
          {
            legalEntityIds: legalEntityOption?.legalEntityIds ?? [],
            eventTypeIds: eventTypeQuickFilter.eventTypes.eventTypeIds,
          },
          searchOptions,
          state.page,
          signal
        );

        if (!signal || !signal.aborted) {
          setState((state) => ({
            ...state,
            items,
            totalItems,
            error: error as string,
          }));
        }
      } catch (error) {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, error: error as string }));
      } finally {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, loading: false }));
      }
    },
    [legalEntityOption?.legalEntityIds, eventTypeQuickFilter.eventTypes.eventTypeIds, state.page]
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    timer = setTimeout(
      () => loadLegalEntityEventsCallback(searchOptions, abortController.signal),
      searchOptions.query ? 500 : 0
    );

    return () => {
      abortController.abort();
      clearTimeout(timer);
    };
  }, [loadLegalEntityEventsCallback, searchOptions]);

  useEffect(() => {
    loadCorporateChangesCategory();
  }, [loadCorporateChangesCategory]);

  return (
    <>
      {hasAccessToPage && (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <ReportTableHeader
            title={`${t(`${tRP}.tab_label`)}`}
            searchOptions={searchOptions}
            handleChangeSearchOptions={handleChangeSearchOptions}
          >
            <QuickfiltersContainer stretched={false}>
              {legalEntityQuickFilter}
              <EventTypeQuickfilter
                quickFilters={eventTypeQuickFilter}
                handleChangeQuickFilters={setEventTypeQuickFilter}
              />
            </QuickfiltersContainer>
          </ReportTableHeader>
          <Subheader>
            <AddNewEvent
              legalEntityId={legalEntityOption?.legalEntityIds[0] ?? ""}
              handleNotifyParent={() => loadLegalEntityEventsCallback()}
            />
            <CommonPagination
              page={state.page.pageNumber + 1}
              handleChangePage={handleChangePageNumber}
              totalItems={state.totalItems}
              defaultPageSize={DEFAULT_PAGE_SIZE}
            />
          </Subheader>
          {state.items?.map((i) => (
            <LegalEntityEvent
              legalEntityEvent={i}
              propsFilter={{
                legalEntityIds: legalEntityOption?.legalEntityIds ?? [],
                eventTypeIds: eventTypeQuickFilter.eventTypes.eventTypeIds,
              }}
              corporateChanges={corporateChanges}
              key={i.id}
              legalEntities={legalEntities}
            />
          ))}
        </LoadingOverlay>
      )}
    </>
  );
};

export default AssembliesAuditsCorporateChanges;
